import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
    AppStatus,
    CodedReference,
    Document,
    DocumentRequirement,
    DocumentRequirementType,
    DocumentTemplate,
    DocumentType,
    QueryPaging,
    SystemEntity,
} from '@wdx/clmi/clmi-swagger-gen';
import { CrudStatus } from '@wdx/shared/utils';
import { Observable } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import * as documentChecklistActions from '../../../../../../state/document-checklist/document-checklist.actions';
import * as documentChecklistSelectors from '../../../../../../state/document-checklist/document-checklist.selectors';
import * as documentTemplatesActions from '../../../../../../state/document-templates/document-templates.actions';
import * as documentTemplatesSelectors from '../../../../../../state/document-templates/document-templates.selectors';
import * as documentTypesActions from '../../../../../../state/document-types/document-types.actions';
import * as documentTypesSelectors from '../../../../../../state/document-types/document-types.selectors';
import * as documentsActions from '../../../../../../state/documents/documents.actions';
import * as documentsSelectors from '../../../../../../state/documents/documents.selectors';

@Injectable({
    providedIn: 'root',
})
export class DocumentsFacadeService {
    documentTemplates: string[] = [];

    constructor(private store$: Store) {}

    getActiveDocumentTypes$(): Observable<DocumentType[]> {
        return this.store$
            .select(documentTypesSelectors.getAllActiveDocumentTypeStatus)
            .pipe(
                tap((status) => {
                    if (status === CrudStatus.Initial) {
                        this.store$.dispatch(
                            documentTypesActions.getAllActiveDocumentTypes(),
                        );
                    }
                }),
                filter((status) => status === CrudStatus.Success),
                switchMap(() =>
                    this.store$.select(
                        documentTypesSelectors.getAllActiveDocumentType,
                    ),
                ),
            );
    }

    getDocumentRequirementTypes$(
        app: AppStatus,
    ): Observable<DocumentRequirementType[]> {
        return this.store$
            .select(documentChecklistSelectors.getDocumentRequirementTypes)
            .pipe(
                tap((documentRequirementTypes) => {
                    if (!documentRequirementTypes) {
                        this.loadDocumentRequirementTypes(app);
                    }
                }),
            );
    }

    getDocumentRequirements$(
        entityId: string,
        entityType: SystemEntity,
    ): Observable<DocumentRequirement[]> {
        return this.store$.select(
            documentChecklistSelectors.getDocumentRequirements,
            {
                entityId,
                entityType,
            },
        );
    }

    getDocumentRequirementsIsLoading$(
        entityId: string,
        entityType: SystemEntity,
    ): Observable<boolean> {
        return this.store$.select(
            documentChecklistSelectors.getDocumentRequirementsIsLoading({
                entityId,
                entityType,
            }),
        );
    }

    getDocumentRequirementsHasError$(
        entityId: string,
        entityType: SystemEntity,
    ): Observable<boolean> {
        return this.store$.select(
            documentChecklistSelectors.getDocumentRequirementsHasError({
                entityId,
                entityType,
            }),
        );
    }

    getDocumentTemplates$(
        documentTypeCode: string,
    ): Observable<DocumentTemplate[]> {
        return this.store$
            .select(
                documentTemplatesSelectors.getDocumentTemplatesList({
                    id: documentTypeCode,
                }),
            )
            .pipe(
                tap(() => {
                    if (!this.documentTemplates.includes(documentTypeCode)) {
                        this.loadDocumentTemplates(documentTypeCode);
                        this.documentTemplates.push(documentTypeCode);
                    }
                }),
            );
    }

    getDocumentsForEntity$(
        entityId: string,
        entityType: SystemEntity,
    ): Observable<Document[]> {
        return this.store$.select(
            documentsSelectors.getDocumentsForEntityId({
                entityType,
                entityId,
            }),
        );
    }

    getDocumentsForEntityIsLoading$(
        entityId: string,
        entityType: SystemEntity,
    ): Observable<boolean> {
        return this.store$.select(
            documentsSelectors.getDocumentsForEntityIdIsLoadingList({
                entityType,
                entityId,
            }),
        );
    }

    getDocumentsForEntityHasError$(
        entityId: string,
        entityType: SystemEntity,
    ): Observable<boolean> {
        return this.store$.select(
            documentsSelectors.getDocumentsForEntityIdHasLoadingListError({
                entityType,
                entityId,
            }),
        );
    }

    loadDocumentTemplates(documentTypeCode: string): void {
        this.store$.dispatch(
            documentTemplatesActions.getDocumentTemplates({ documentTypeCode }),
        );
    }

    loadDocumentTypes(): void {
        this.store$.dispatch(documentTypesActions.getAll({ reset: true }));
    }

    updateDocumentTypes(paging: QueryPaging): void {
        if (paging?.page < paging?.totalPages) {
            this.store$.dispatch(
                documentTypesActions.updateDocumentTypes({
                    paginationOptions: {
                        ...paging,
                        pageNumber: paging.page,
                    },
                }),
            );
        }
    }

    loadDocumentRequirementTypes(app: AppStatus): void {
        this.store$.dispatch(
            documentChecklistActions.getDocumentRequirementTypes({ app }),
        );
    }

    loadDocumentRequirements(
        entityId: string,
        entityType: SystemEntity,
        postData: string[],
    ): void {
        this.store$.dispatch(
            documentChecklistActions.getDocumentRequirements({
                entityId,
                entityType,
                data: postData,
            }),
        );
    }

    loadDocumentsForEntity(entityId: string, entityType: SystemEntity): void {
        this.store$.dispatch(
            documentsActions.getDocumentsForEntityId({
                entityId,
                entityType,
            }),
        );
    }

    approveDocumentRequirement(documentRequirementIds: string[]): void {
        this.store$.dispatch(
            documentChecklistActions.approveDocumentRequirement({
                documentRequirementIds,
                approve: true,
            }),
        );
    }

    cancelDocumentRequirement(
        documentRequirementIds: string[],
        entityType: SystemEntity,
        entityId: string,
    ): void {
        this.store$.dispatch(
            documentChecklistActions.cancelDocumentRequirement({
                documentRequirementIds,
                entityType,
                entityId,
            }),
        );
    }

    deleteDocumentRequirement(
        documentRequirementId: string,
        entityType: SystemEntity,
        entityId: string,
    ): void {
        this.store$.dispatch(
            documentChecklistActions.deleteDocumentRequirement({
                documentRequirementId,
                entityType,
                entityId,
            }),
        );
    }

    sendDocumentRequirement(documentRequirementIds: string[]): void {
        this.store$.dispatch(
            documentChecklistActions.sendDocumentRequirement({
                documentRequirementIds,
            }),
        );
    }

    createDocumentExternalTask(
        documentRequirementId: string,
        entityType: SystemEntity,
        entityId: string,
        externalUserPartyId: string,
    ): void {
        this.store$.dispatch(
            documentChecklistActions.createDocumentExternalTask({
                documentRequirementId,
                entityType,
                entityId,
                externalUserPartyId,
            }),
        );
    }

    generateDocumentFromTemplate(
        payload: {
            documentRequirementId: string;
            documentTemplateId: string;
        }[],
    ): void {
        this.store$.dispatch(
            documentChecklistActions.generateDocumentFromTemplate({ payload }),
        );
    }

    addDocumentRequirement(
        documentRequirementType: CodedReference,
        entity: SystemEntity,
        entityRef: CodedReference,
        relatedEntity?: SystemEntity,
        relatedEntityRef?: CodedReference,
        additionalEntities?: {
            entity: SystemEntity;
            entityRef: CodedReference;
        }[],
    ): void {
        this.store$.dispatch(
            documentChecklistActions.addDocumentRequirement({
                documentRequirementType,
                entity,
                entityRef,
                relatedEntity,
                relatedEntityRef,
                ...(additionalEntities ? { additionalEntities } : {}),
            }),
        );
    }

    rejectDocumentRequirement(documentRequirementIds: string[]): void {
        this.store$.dispatch(
            documentChecklistActions.approveDocumentRequirement({
                documentRequirementIds,
                approve: false,
            }),
        );
    }

    linkDocumentToRequirement(
        documentRequirementId: string,
        documentId: string,
        isLinked = true,
    ): void {
        this.store$.dispatch(
            documentChecklistActions.linkDocumentToRequirement({
                documentRequirementId,
                documentId,
                isLinked,
            }),
        );
    }
}
